/* ═══ AgChip — Anagogia Design System ═══ */

import { AG_LABELS } from '../utils/agLabels';

import styles from './AgChip.module.css';

interface AgChipProps {
  /** Text content displayed in the chip */
  label: string;
  /** Visual style */
  variant?: 'filled' | 'outlined';
  /** Semantic color */
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'warning'
    | 'info';
  /** Size variant */
  size?: 'sm' | 'md';
  /** Optional leading icon */
  icon?: React.ReactNode;
  /** Show delete button; called on click */
  onDelete?: () => void;
  /** Nom accessible du bouton de retrait (défaut : « Retirer <libellé> ») */
  deleteLabel?: string;
  /** Makes the chip interactive (renders as <button>) */
  onClick?: () => void;
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

/**
 * Pastille compacte pour étiquettes, filtres et sélections.
 * @category Affichage
 */
function AgChip({
  label,
  variant = 'filled',
  color = 'default',
  size = 'md',
  icon,
  onDelete,
  deleteLabel,
  onClick,
  className,
  style,
}: AgChipProps) {
  const isClickable = !!onClick;

  const rootClass = [
    styles.chip,
    styles[variant],
    styles[color],
    styles[size],
    isClickable && styles.clickable,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  const content = (
    <>
      {icon && <span className={styles.icon}>{icon}</span>}
      <span className={styles.label}>{label}</span>
      {onDelete && (
        <button
          type="button"
          className={styles.deleteButton}
          onClick={handleDelete}
          aria-label={deleteLabel ?? AG_LABELS.retirer(label)}
        >
          &times;
        </button>
      )}
    </>
  );

  if (isClickable) {
    return (
      <button type="button" className={rootClass} onClick={onClick} style={style}>
        {content}
      </button>
    );
  }

  return <span className={rootClass} style={style}>{content}</span>;
}

export { AgChip };
export type { AgChipProps };
export default AgChip;
